import { useEffect, useState, Suspense } from 'react';
import { lazyRetry as lazy } from '@/lib/lazyRetry';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { HomeNav } from './HomeNav';
import { Hero, WorksWith } from './Hero';
import { MeetVela } from './MeetVela';
const DemandPreview = lazy(() => import('@/components/DemandPreview'));
import api from '@/lib/api';
const BelowFold = lazy(() => import('./BelowFold'));
import { VisitorAssistant } from '@/components/assistant/VisitorAssistant';
import { captureUtm, track } from './track';
import { useDeferredMount } from './useDeferredMount';

const SAVINGS_PLANS = [
  { id: 'starter', label: 'Starter', name: 'Starter', price: 497 },
  { id: 'growth', label: 'Growth', name: 'Growth', price: 1197 },
  { id: 'command', label: 'Command', name: 'Command', price: 2497 },
  { id: 'scale', label: 'Scale', name: 'Scale', price: 4997 },
  { id: 'enterprise', label: 'Enterprise', name: 'Enterprise', price: 9997 },
];

const StickyCTA = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 750);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show || dismissed) return null;
  return (
    <div data-testid="sticky-cta" className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:left-6 z-40 sm:w-auto">
      <div className="flex items-center gap-3 rounded-2xl bg-[#0d0d14]/95 backdrop-blur-xl border border-violet-500/30 shadow-2xl px-4 py-3">
        <p className="text-[13px] text-white/75 font-medium hidden sm:block">Ready to put Veluro to work?</p>
        <button data-testid="sticky-cta-trial" onClick={() => { track('sticky_trial_clicked'); navigate('/pricing'); }}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:brightness-110 text-white font-semibold px-5 py-2.5 text-[13.5px] transition-[filter]">
          Start Free Trial <ArrowRight className="h-3.5 w-3.5" />
        </button>
        <button data-testid="sticky-cta-close" onClick={() => setDismissed(true)} className="text-white/35 hover:text-white/70 shrink-0"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const [demand, setDemand] = useState(null);

  // Heavy blocks (framer-motion / recharts / chat markdown) are deferred so they never
  // download during the first-paint window — they mount on scroll-proximity, on the first
  // visitor interaction, or via a staggered idle fallback (keeps SEO content present).
  const [demandRef, showDemand] = useDeferredMount({ idleMs: 2200 });
  const [belowRef, showBelow] = useDeferredMount({ idleMs: 3200 });

  useEffect(() => {
    captureUtm();
    track('homepage_viewed');
    document.title = 'Veluro — Your AI-Powered Growth Team';
    const sid = localStorage.getItem('veluro_demand_session');
    if (sid) api.get(`/public/demand/session/${sid}`).then((r) => setDemand(r.data)).catch(() => {});
    if (window.location.hash) {
      const sel = window.location.hash;
      let tries = 0;
      const iv = setInterval(() => {
        const el = document.querySelector(sel);
        if (el) { el.scrollIntoView({ behavior: 'smooth' }); clearInterval(iv); }
        else if (++tries > 20) clearInterval(iv);
      }, 150);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#07070b] text-white antialiased relative overflow-x-hidden">
      {/* global purple ambiance */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-1/3 left-[-180px] w-[420px] h-[520px] bg-violet-600/[0.05] rounded-full blur-[140px]" />
        <div className="absolute bottom-1/4 right-[-160px] w-[420px] h-[480px] bg-fuchsia-600/[0.04] rounded-full blur-[140px]" />
      </div>
      <HomeNav />
      <main className="relative">
        <Hero />
        <WorksWith />
        <MeetVela />
        <div id="demand-analyzer" ref={demandRef} className="relative">
          {showDemand
            ? <Suspense fallback={<div className="min-h-[300px]" />}><DemandPreview /></Suspense>
            : <div className="min-h-[300px]" />}
        </div>
        <div ref={belowRef} aria-hidden="true" />
        {showBelow && (
          <Suspense fallback={<div className="min-h-screen" />}>
            <BelowFold demand={demand} plans={SAVINGS_PLANS}
              onSavingsCta={() => { track('savings_trial_clicked'); navigate('/pricing'); }} />
          </Suspense>
        )}
      </main>
      <StickyCTA />
      <VisitorAssistant />
    </div>
  );
}
